import React, { useState, useEffect, useRef } from 'react';

const BusquedaEtiquetasV2 = () => {
  // Obtener fecha actual en formato YYYYMMDD
  const getToday = () => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    return `${year}${month}${day}`;
  };

  const [formData, setFormData] = useState({
    fechaIni: getToday(),
    fechaFin: getToday()
  });
  const [showCalendarIni, setShowCalendarIni] = useState(false);
  const [showCalendarFin, setShowCalendarFin] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);
  const [expandedLotes, setExpandedLotes] = useState({});
  const [printData, setPrintData] = useState(null);
  
  // Para cancelar requests pendientes
  const [abortController, setAbortController] = useState(null);

  // Referencias para los calendarios
  const calendarIniRef = useRef(null);
  const calendarFinRef = useRef(null);
  const inputIniRef = useRef(null);
  const inputFinRef = useRef(null);

  // Materiales de empaque predefinidos
  const materialesEmpaque = [
    { codigo: 'MEMP000356', descripcion: 'Caja # 13, Tapa, Test 200' },
    { codigo: 'MEMP000365', descripcion: 'Caja # 13, Fondo, Test 275' },
    { codigo: 'MEMP000417', descripcion: 'Bolsas Especiales - 20 x 26' },
    { codigo: 'MEMP000738', descripcion: 'Etiqueta Vencimiento Grandes 2026' },
    { codigo: 'MEMP000507', descripcion: 'Grapas C-58 5/8 Marca ZEUS' },
    { codigo: 'MEMP001273', descripcion: 'Etiqueta Polipropileno 50* 100 mm' },
    { codigo: 'MEMP000737', descripcion: 'Cajas para reutilizar' },
    { codigo: 'MEMP000415', descripcion: 'Bolsa 26 Libras - 20 x 26' },
    { codigo: 'MEMP000148', descripcion: 'Bolsa para Blocks de 5 Libras - 10 x 14' }
  ];

  // Funciones auxiliares
  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString('es-GT', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateStr;
    }
  };

  const formatSimpleDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString('es-GT');
    } catch {
      return dateStr;
    }
  };

  // Convertir fecha YYYYMMDD a Date object
  const parseFechaInput = (fechaStr) => {
    if (fechaStr && fechaStr.length === 8) {
      const year = parseInt(fechaStr.substring(0, 4));
      const month = parseInt(fechaStr.substring(4, 6)) - 1;
      const day = parseInt(fechaStr.substring(6, 8));
      return new Date(year, month, day);
    }
    return new Date();
  };

  // Convertir Date a YYYYMMDD
  const formatFechaInput = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}${month}${day}`;
  };

  // Validar formato de fecha YYYYMMDD
  const isValidDateFormat = (dateStr) => {
    if (!dateStr || dateStr.length !== 8) return false;
    
    const year = parseInt(dateStr.substring(0, 4));
    const month = parseInt(dateStr.substring(4, 6));
    const day = parseInt(dateStr.substring(6, 8));
    
    if (isNaN(year) || isNaN(month) || isNaN(day)) return false;
    if (month < 1 || month > 12) return false;
    if (day < 1 || day > 31) return false;
    
    return true;
  };

  // Obtener fecha para el calendario
  const getCalendarDate = (fechaStr, isStartDate) => {
    if (isValidDateFormat(fechaStr)) {
      return parseFechaInput(fechaStr);
    }
    return new Date();
  };

  // Generar calendario para un mes específico
  const generateCalendar = (date, isStartDate = true) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const startingDay = firstDay.getDay();
    const monthLength = lastDay.getDate();
    
    const today = new Date();
    const selectedDate = parseFechaInput(isStartDate ? formData.fechaIni : formData.fechaFin);
    
    const days = [];
    
    // Días de la semana
    const weekDays = ['Do', 'Lu', 'Ma', 'Mi', 'Ju', 'Vi', 'Sa'];
    
    // Espacios vacíos al inicio
    for (let i = 0; i < startingDay; i++) {
      days.push(<div key={`empty-${i}`} className="calendar-day empty"></div>);
    }
    
    // Días del mes
    for (let day = 1; day <= monthLength; day++) {
      const currentDate = new Date(year, month, day);
      const isToday = currentDate.toDateString() === today.toDateString();
      const isSelected = selectedDate && currentDate.toDateString() === selectedDate.toDateString();
      
      days.push(
        <div
          key={`day-${day}`}
          className={`calendar-day ${isToday ? 'today' : ''} ${isSelected ? 'selected' : ''}`}
          onClick={() => handleDateSelect(currentDate, isStartDate)}
          style={{
            padding: '8px',
            textAlign: 'center',
            cursor: 'pointer',
            borderRadius: '4px',
            backgroundColor: isSelected ? '#3498db' : isToday ? '#e3f2fd' : 'transparent',
            color: isSelected ? 'white' : isToday ? '#1976d2' : '#333',
            fontWeight: isSelected || isToday ? 'bold' : 'normal'
          }}
        >
          {day}
        </div>
      );
    }
    
    return (
      <div 
        ref={isStartDate ? calendarIniRef : calendarFinRef}
        style={{
          backgroundColor: 'white',
          border: '1px solid #ddd',
          borderRadius: '8px',
          padding: '15px',
          boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
          position: 'absolute',
          top: '100%',
          left: 0,
          zIndex: 1000,
          minWidth: '280px'
        }}>
        {/* Header del calendario */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '15px'
        }}>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              handleMonthChange(-1, isStartDate);
            }}
            style={{
              background: 'none',
              border: 'none',
              fontSize: '18px',
              cursor: 'pointer',
              padding: '5px 10px'
            }}
          >
            ‹
          </button>
          <div style={{ fontWeight: 'bold', fontSize: '16px' }}>
            {date.toLocaleDateString('es-GT', { month: 'long', year: 'numeric' })}
          </div>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              handleMonthChange(1, isStartDate);
            }}
            style={{
              background: 'none',
              border: 'none',
              fontSize: '18px',
              cursor: 'pointer',
              padding: '5px 10px'
            }}
          >
            ›
          </button>
        </div>
        
        {/* Días de la semana */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(7, 1fr)',
          gap: '5px',
          marginBottom: '10px'
        }}>
          {weekDays.map(day => (
            <div key={day} style={{
              textAlign: 'center',
              fontWeight: 'bold',
              fontSize: '12px',
              color: '#666',
              padding: '5px'
            }}>
              {day}
            </div>
          ))}
        </div>
        
        {/* Días del mes */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(7, 1fr)',
          gap: '5px'
        }}>
          {days}
        </div>
      </div>
    );
  };

  // Estados para las fechas del calendario
  const [calendarDateIni, setCalendarDateIni] = useState(() => getCalendarDate(formData.fechaIni, true));
  const [calendarDateFin, setCalendarDateFin] = useState(() => getCalendarDate(formData.fechaFin, false));

  const handleDateSelect = (date, isStartDate) => {
    const formattedDate = formatFechaInput(date);
    if (isStartDate) {
      setFormData(prev => ({ ...prev, fechaIni: formattedDate }));
      setShowCalendarIni(false);
      setCalendarDateIni(date);
    } else {
      setFormData(prev => ({ ...prev, fechaFin: formattedDate }));
      setShowCalendarFin(false);
      setCalendarDateFin(date);
    }
  };

  const handleMonthChange = (direction, isStartDate) => {
    if (isStartDate) {
      const newDate = new Date(calendarDateIni);
      newDate.setMonth(newDate.getMonth() + direction);
      setCalendarDateIni(newDate);
    } else {
      const newDate = new Date(calendarDateFin);
      newDate.setMonth(newDate.getMonth() + direction);
      setCalendarDateFin(newDate);
    }
  };

  // Efecto para sincronizar calendarios cuando cambian las fechas manualmente
  useEffect(() => {
    if (isValidDateFormat(formData.fechaIni)) {
      const newDate = parseFechaInput(formData.fechaIni);
      setCalendarDateIni(newDate);
    }
  }, [formData.fechaIni]);

  useEffect(() => {
    if (isValidDateFormat(formData.fechaFin)) {
      const newDate = parseFechaInput(formData.fechaFin);
      setCalendarDateFin(newDate);
    }
  }, [formData.fechaFin]);

  // Parsear detalles y hacer match con CD_CE_CODIGO
  const parseDetalles = (detalleStr, ceCodigo) => {
    if (!detalleStr) return null;
    try {
      const detalles = JSON.parse(detalleStr);
      return detalles.filter(detalle => detalle.CD_CE_CODIGO === ceCodigo);
    } catch {
      return null;
    }
  };

  // Calcular total de peso por lote interno
  const calcularPesoTotalLote = (lotItems) => {
    return lotItems.reduce((total, item) => total + (item.CD_PESO || 0), 0);
  };

  // Obtener información del lote interno
  const getLotSummary = (lotId, lotItems) => {
    if (!lotItems || lotItems.length === 0) return null;
    
    const firstItem = lotItems[0];
    const totalPeso = calcularPesoTotalLote(lotItems);
    const totalCajillas = lotItems.length;
    
    // Extraer el lote interno real (sin el sufijo del producto)
    const realLotId = lotId.includes('_') ? lotId.split('_')[0] : lotId;
    
    return {
      productName: firstItem.parentItem?.CE_NOMBRE_PRODUCTO || 'N/A',
      productCode: firstItem.parentItem?.CE_PRODUCTO || 'N/A',
      totalPeso,
      totalCajillas,
      creationDate: firstItem.parentItem?.CE_FECHACREACION || 'N/A',
      user: firstItem.parentItem?.CE_USUARIOCREACION || 'N/A',
      lotId: realLotId,
      uniqueKey: lotId
    };
  };

  // Agrupar registros por lote interno
  const groupedByInternalLot = result && Array.isArray(result) ? 
    result.reduce((acc, item) => {
      const detalles = parseDetalles(item.CE_DETALLE, item.CE_CODIGO);
      if (detalles && detalles.length > 0) {
        detalles.forEach(detalle => {
          const productCode = item.CE_PRODUCTO || 'SIN_PRODUCTO';
          const internalLot = detalle.CD_LOTE;
          const uniqueKey = `${internalLot}_${productCode}`;
          
          if (!acc[uniqueKey]) {
            acc[uniqueKey] = [];
          }
          acc[uniqueKey].push({
            ...detalle,
            parentItem: item
          });
        });
      }
      return acc;
    }, {}) : {};

  // Función para preparar datos de impresión
// Función para preparar datos de impresión - VERSIÓN CORREGIDA (SIN SUMAR REPETIDOS)
const preparePrintData = (lotId, lotItems) => {
  console.log('🔧 Preparando datos para impresión, lote:', lotId);
  
  if (!lotItems || lotItems.length === 0) {
    console.log('❌ No hay items para imprimir');
    return null;
  }

  // PASO 1: Crear un Set para rastrear combinaciones únicas de número de cajilla + peso
  const combinacionesUnicas = new Set();
  const cajillasUnicas = {};
  let pesoTotalReal = 0;
  
  lotItems.forEach(item => {
    const numCajilla = item.CD_NUM_CAJILLA;
    const peso = item.CD_PESO || 0;
    
    if (numCajilla) {
      // Crear una clave única combinando número de cajilla y peso
      const claveUnica = `${numCajilla}_${peso}`;
      
      // Si esta combinación no existe, la agregamos
      if (!combinacionesUnicas.has(claveUnica)) {
        combinacionesUnicas.add(claveUnica);
        
        // Guardamos el item (solo una vez por combinación única)
        cajillasUnicas[numCajilla] = {
          ...item,
          CD_PESO: peso
        };
        
        pesoTotalReal += peso;
        console.log(`✅ Cajilla #${numCajilla} con peso ${peso} kg agregada (única)`);
      } else {
        console.log(`⚠️ Cajilla #${numCajilla} con peso ${peso} kg duplicada - IGNORADA`);
      }
    }
  });

  console.log('📊 Cajillas después de filtrar duplicados:', Object.keys(cajillasUnicas).length, 'de', lotItems.length, 'registros originales');

  const summary = getLotSummary(lotId, lotItems);
  if (!summary) {
    console.log('❌ No se pudo generar el summary');
    return null;
  }

  console.log('✅ Summary generado:', summary);
  console.log('✅ Peso total (sin duplicados):', pesoTotalReal);

  const printData = {
    lotId: summary.lotId,
    productName: summary.productName,
    productCode: summary.productCode,
    user: summary.user,
    creationDate: summary.creationDate,
    totalPeso: pesoTotalReal, // Peso sin duplicados
    totalCajillas: Object.keys(cajillasUnicas).length, // Contamos cajillas únicas por combinación
    det: cajillasUnicas
  };

  return printData;
};


  // Función para obtener fecha de producción (un día antes de la fecha del lote)
  const getFechaProduccion = (fechaLote) => {
    if (!fechaLote) return 'N/A';
    
    try {
      const fecha = new Date(fechaLote);
      fecha.setDate(fecha.getDate() - 1);
      
      return fecha.toLocaleDateString('es-GT', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      });
    } catch (error) {
      console.error('Error al calcular fecha de producción:', error);
      return 'N/A';
    }
  };

  // Función para imprimir - SIMPLIFICADA
  const handlePrint = (lotId, lotItems) => {
    console.log('🖨️ Iniciando impresión para lote:', lotId);
    
    const printDataItem = preparePrintData(lotId, lotItems);
    
    if (!printDataItem) {
      console.log('❌ No hay datos válidos para imprimir');
      alert('No hay datos válidos para imprimir');
      return;
    }

    console.log('📊 Datos preparados:', printDataItem);

    // Crear una ventana nueva para imprimir
    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      alert('Por favor permite ventanas emergentes para imprimir');
      return;
    }

    // Generar el contenido HTML para imprimir
  const printContent = `
<!DOCTYPE html>
<html>
<head>
  <title>Formato de Peso - Lote ${printDataItem.lotId}</title>
  <style>
    body {
      font-family: Arial, sans-serif;
      font-size: 12px; /* Reducido de 11px */
      line-height: 1.1; /* Reducido de 1.2 */
      margin: 0;
      padding: 2mm; /* Reducido de 4mm */
      color: black;
      background: white;
    }
    .print-container {
      width: 100%;
      height: auto;
      transform: scale(0.95); /* Escala todo el contenido */
      transform-origin: top left;
    }
    .header {
      display: flex;
      justify-content: space-between;
      align-items: flex-start;
      margin-bottom: 8px; /* Reducido */
      border-bottom: 1px solid #000; /* Reducido */
      padding-bottom: 4px; /* Reducido */
    }
    table {
      width: 100%;
      border-collapse: collapse;
      margin-bottom: 5px; /* Reducido */
      font-size: 10px; /* Tamaño específico para tablas */
    }
    th, td {
      border: 0.5px solid #000; /* Líneas más delgadas */
      padding: 1px 2px; /* Reducido */
      text-align: center;
    }
    th {
      background-color: #f5f5f5;
      font-weight: bold;
      padding: 2px 1px; /* Reducido */
    }
    .notes {
      font-size: 7px; /* Reducido */
      margin-bottom: 4px; /* Reducido */
      line-height: 1.2; /* Reducido */
    }
    .signature-box {
      border-bottom: 0.5px solid black; /* Reducido */
      height: 10px; /* Reducido */
      margin-bottom: 1px; /* Reducido */
      width: 80%;
      margin: 0 auto;
    }
    .footer {
      text-align: center;
      margin-top: 8px; /* Reducido */
      border-top: 1px solid #000; /* Reducido */
      padding-top: 4px; /* Reducido */
      font-size: 7px; /* Reducido */
      font-weight: bold;
    }
    
    /* Reducir alturas de filas */
    tr {
      height: 10px !important; /* Reducido de 12px */
    }
    
    /* Ajustes específicos para headers */
    .header-left div,
    .header-right div {
      margin-bottom: 1px; /* Reducido espacio entre líneas */
    }
    
    @media print {
      body { 
        margin: 0; 
        padding: 0; 
        size: A4 portrait;
      }
      .print-container { 
        page-break-after: avoid; 
        width: 100%;
        height: 100%;
      }
    }
  </style>
</head>
<body>
  <div class="print-container">
    <!-- ENCABEZADO DE EMPRESA CON LOGO Y CÓDIGO -->
    <div class="header">
      <!-- LADO IZQUIERDO - INFORMACIÓN DE EMPRESA -->
      <div class="header-left">
        <div style="font-size: 11px; font-weight: bold;"> <!-- Reducido -->
          Fabrica de productos Lacteos Parma. S.A.
        </div>
        <div style="font-size: 10px; font-weight: bold;"> <!-- Reducido -->
          Departamento de Produccion
        </div>
        <div style="font-size: 9px;"> <!-- Reducido -->
          Area: Cocina - Peso de formas
        </div>
      </div>

      <!-- LADO DERECHO - CÓDIGO Y VERSIÓN -->
      <div class="header-right">
        <div style="font-size: 9px; font-weight: bold;"> <!-- Reducido -->
          CODIGO: R.PD.012.0300
        </div>
        <div style="font-size: 9px; font-weight: bold;"> <!-- Reducido -->
          Versión: 3.0
        </div>
      </div>
    </div>

    <!-- ENCABEZADO CON ESTRUCTURA ORIGINAL Y LÍNEAS -->
    <table>
      <tbody>
        <tr>
          <td width="25%"><strong>Fecha:</strong></td>
          <td width="25%">${getFechaProduccion(printDataItem.creationDate)}</td>
          <td width="25%" style="font-weight: bold;">Producción</td>
          <td width="25%" style="font-weight: bold;">Información</td>
        </tr>
        <tr>
          <td style="font-weight: bold;">Lote:</td>
          <td>${printDataItem.lotId}</td>
          <td>#OF- Sap</td>
          <td></td>
        </tr>
        <tr>
          <td style="font-weight: bold;">Encajador:</td>
          <td>${printDataItem.user}</td>
          <td>Bodega</td>
          <td></td>
        </tr>
        <tr>
          <td style="font-weight: bold;">Producto:</td>
          <td colspan="3">${printDataItem.productCode} - ${printDataItem.productName}</td>
        </tr>
      </tbody>
    </table>

    <!-- TABLA PRINCIPAL CON 38 FILAS Y LÍNEAS -->
    <table>
      <thead>
        <tr>
          <th width="8%"># Forma</th>
          <th width="12%">Peso KG</th>
          <th width="15%">Rev Visual B-M</th>
          <th width="12%">Ingreso √</th>
          <th width="15%">Fecha Salida</th>
          <th width="18%">Área/Destino</th>
          <th width="20%">Responsable</th>
        </tr>
      </thead>
      <tbody>
        ${Array.from({ length: 38 }, (_, i) => i + 1).map(num => `
          <tr>
            <td>${num}</td>
            <td>${printDataItem.det && printDataItem.det[num] ? 
              (parseFloat(printDataItem.det[num].CD_PESO) || '').toFixed(2) : ''}</td>
            <td></td>
            <td></td>
            <td></td>
            <td></td>
            <td></td>
          </tr>
        `).join('')}
        <tr style="font-weight: bold;">
          <td>Total</td>
          <td>${printDataItem.totalPeso.toFixed(2)}</td>
          <td></td>
          <td></td>
          <td></td>
          <td></td>
          <td></td>
        </tr>
      </tbody>
    </table>

    <!-- INFORMACIÓN DE TARIMA CON LÍNEAS -->
    <table>
      <tbody>
        <tr>
          <td width="20%" style="font-weight: bold;">Tarima No.</td>
          <td width="30%"></td>
          <td width="50%" colspan="2">
            LIBERADO __________________ REPROCESO ____________________
          </td>
        </tr>
      </tbody>
    </table>

    <!-- NOTAS COMPLETAS -->
    <div class="notes">
      <p style="margin: 1px 0;">
        <strong>*Notas:</strong> Colocar B= Bueno, M= Malo. Si se detecta algún contaminante, identificar inmediatamente e informar a Supervisión y/o Control de Calidad sobre el hallazgo.
      </p>
      <p style="margin: 1px 0;">
        *Para tarimas, revisar que estén completas, sin astillas o clavos salidos, en buen estado, colocar B= Bueno, M= Malo  Bueno____, Malo____,
      </p>
    </div>

    <!-- FIRMAS CON LÍNEAS -->
    <table class="signatures">
      <tbody>
        <tr>
          <td width="25%" style="text-align: center;">
            <div class="signature-box"></div>
            Vo. Bo. Encajador
          </td>
          <td width="25%" style="text-align: center;">
            <div class="signature-box"></div>
            Vo. Bo. Supervisor
          </td>
          <td width="25%" style="text-align: center;">
            <div class="signature-box"></div>
            Vo. Bo. Bodega
          </td>
          <td width="25%" style="text-align: center;">
            <div class="signature-box"></div>
            Vo. Bo. Calidad
          </td>
        </tr>
      </tbody>
    </table>

    <!-- MATERIAL DE EMPAQUE CON LÍNEAS -->
    <div>
      <h6 style="font-size: 8px; text-align: center; margin: 2px 0; font-weight: bold;">
        CONSUMO MATERIAL DE EMPAQUE
      </h6>
      <table>
        <thead>
          <tr>
            <th width="15%">UNIDADES</th>
            <th width="20%">CODIGO</th>
            <th width="45%">DESCRIPCIÓN</th>   
            <th width="20%">LOTE</th>
          </tr>
        </thead>
        <tbody>
          ${materialesEmpaque.map(material => `
            <tr>
              <td></td>
              <td>${material.codigo}</td>
              <td style="text-align: left;">${material.descripcion}</td>
              <td></td>
            </tr>
          `).join('')}
        </tbody>
      </table>
    </div>

    <!-- PIE DE PÁGINA - INFORMACIÓN CONFIDENCIAL -->
    <div class="footer">
      <div>Esta información es exclusiva y confidencial de Fábrica de Productos Lácteos Parma S.A.</div>
      <div>Prohibido la divulgación distribución y/o reproducción parcial o total de este documento.</div>
    </div>
  </div>

  <script>
    window.onload = function() {
      setTimeout(function() {
        window.print();
        setTimeout(function() {
          window.close();
        }, 500);
      }, 250);
    };
  </script>
</body>
</html>
`;

    // Escribir el contenido en la ventana nueva
    printWindow.document.open();
    printWindow.document.write(printContent);
    printWindow.document.close();
  };

  // Cleanup effect
  useEffect(() => {
    return () => {
      if (abortController) {
        abortController.abort();
      }
    };
  }, [abortController]);

  // Cerrar calendarios al hacer click fuera
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showCalendarIni && 
          calendarIniRef.current && 
          !calendarIniRef.current.contains(event.target) &&
          inputIniRef.current && 
          !inputIniRef.current.contains(event.target)) {
        setShowCalendarIni(false);
      }
      
      if (showCalendarFin && 
          calendarFinRef.current && 
          !calendarFinRef.current.contains(event.target) &&
          inputFinRef.current && 
          !inputFinRef.current.contains(event.target)) {
        setShowCalendarFin(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showCalendarIni, showCalendarFin]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const toggleLote = (lotId) => {
    setExpandedLotes(prev => ({
      ...prev,
      [lotId]: !prev[lotId]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault(); 
    
    // Cancelar request anterior si existe
    if (abortController) {
      abortController.abort();
    }
    
    // Crear nuevo abort controller
    const controller = new AbortController();
    setAbortController(controller);
    
    setLoading(true);
    setError(null);
    setResult(null);
    setExpandedLotes({});
    setPrintData(null);
    
    console.log('🔸 Fechas enviadas:', formData);

    const datos = {
      "Token": "LJKNFUH2FWEJBAEFFFFFKJ VGDEA003CSFCAQWEUIRRRRRRRRRRRADFYGBIFHVBESRNYHIOPWJROTY4WY",
      "Ambiente": "DESA",
      "Dispositivo": {
        "Marca": "SAMSUNG",
        "Modelo": "S22002B",
        "Version": "Android 18.125",
        "McAdress": null,
        "PowerStatus": "84%",
        "WifiStatus": "Conected to iphone de Mauricio",
        "GPSStatus": "ON",
        "GPS": "321|-485|321"
      },
      "Usr": {
        "Codigo": "",
        "UserNickName": "pierril"
      },
      "DatosMetodo": {
        "NombreMetodo": "BusquedaEtiquetasV2",
        "Datos_Recibidos": JSON.stringify({
          "Fechaini": formData.fechaIni,
          "Fechafin": formData.fechaFin
        })
      }
    };

    try {
      const url = `https://appcorp.parma.com.gt/API/ReceptorDatos?Datos=${encodeURIComponent(JSON.stringify(datos))}`;
      
      const response = await fetch(url, { 
        method: 'GET',
        headers: {
          'Accept': 'application/json',
        },
        signal: controller.signal
      });
      
      if (controller.signal.aborted) {
        return;
      }
      
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Error HTTP: ${response.status} - ${errorText}`);
      }
      
      const data = await response.json(); 
      
      if (controller.signal.aborted) {
        return;
      }
      
      // EXTRAER LOS DATOS REALES Y FILTRAR SOLO PPPL
      let datosReales = [];
      if (data && data.datos_Generados) {
        if (typeof data.datos_Generados === 'string') {
          try {
            const todosDatos = JSON.parse(data.datos_Generados);
            datosReales = todosDatos.filter(item => 
              item.CE_PRODUCTO && item.CE_PRODUCTO.startsWith('PPPL')
            );
          } catch (parseError) {
            throw new Error('Error al procesar los datos del servidor');
          }
        } else {
          datosReales = data.datos_Generados.filter(item => 
            item.CE_PRODUCTO && item.CE_PRODUCTO.startsWith('PPPL')
          );
        }
      }
      
      setResult(datosReales);
      
    } catch (err) {
      if (err.name !== 'AbortError') {
        setError(`Error: ${err.message}`);
      }
    } finally {
      if (!abortController?.signal.aborted) {
        setLoading(false);
      }
    }
  };

  return (
    <div style={{ padding: '20px', fontFamily: 'Arial, sans-serif', backgroundColor: '#f5f5f5', minHeight: '100vh' }}>
      <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
        
        {/* Header */}
        <div style={{ 
          backgroundColor: 'white', 
          padding: '20px', 
          borderRadius: '10px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
          marginBottom: '20px'
        }}>
          <h1 style={{ color: '#2c3e50', textAlign: 'center', marginBottom: '10px' }}>
            🏷️ Control de Pesos PPPL
          </h1>
          <p style={{ textAlign: 'center', color: '#7f8c8d', marginBottom: '20px' }}>
            Detalle de cajillas pesadas - Solo productos PPPL
          </p>

          {/* Formulario */}
          <div style={{ 
            maxWidth: '500px', 
            margin: '0 auto'
          }}>
            <form onSubmit={handleSubmit}>
              <div style={{ display: 'flex', gap: '10px', marginBottom: '15px' }}>
                <div style={{ flex: 1, position: 'relative' }}>
                  <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold', fontSize: '14px' }}>
                    Fecha Inicio:
                  </label>
                  <div style={{ position: 'relative' }}>
                    <input
                      ref={inputIniRef}
                      type="text"
                      name="fechaIni"
                      value={formData.fechaIni}
                      onChange={handleChange}
                      onFocus={() => {
                        setShowCalendarIni(true);
                        if (isValidDateFormat(formData.fechaIni)) {
                          setCalendarDateIni(parseFechaInput(formData.fechaIni));
                        }
                      }}
                      style={{
                        width: '100%',
                        padding: '10px 40px 10px 10px',
                        border: '1px solid #ddd',
                        borderRadius: '6px',
                        fontSize: '14px'
                      }}
                      placeholder="YYYYMMDD"
                    />
                    <span
                      style={{
                        position: 'absolute',
                        right: '10px',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        cursor: 'pointer',
                        fontSize: '18px'
                      }}
                      onClick={() => {
                        setShowCalendarIni(!showCalendarIni);
                        if (isValidDateFormat(formData.fechaIni)) {
                          setCalendarDateIni(parseFechaInput(formData.fechaIni));
                        }
                      }}
                    >
                      📅
                    </span>
                  </div>
                  {showCalendarIni && generateCalendar(calendarDateIni, true)}
                </div>

                <div style={{ flex: 1, position: 'relative' }}>
                  <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold', fontSize: '14px' }}>
                    Fecha Fin:
                  </label>
                  <div style={{ position: 'relative' }}>
                    <input
                      ref={inputFinRef}
                      type="text"
                      name="fechaFin"
                      value={formData.fechaFin}
                      onChange={handleChange}
                      onFocus={() => {
                        setShowCalendarFin(true);
                        if (isValidDateFormat(formData.fechaFin)) {
                          setCalendarDateFin(parseFechaInput(formData.fechaFin));
                        }
                      }}
                      style={{
                        width: '100%',
                        padding: '10px 40px 10px 10px',
                        border: '1px solid #ddd',
                        borderRadius: '6px',
                        fontSize: '14px'
                      }}
                      placeholder="YYYYMMDD"
                    />
                    <span
                      style={{
                        position: 'absolute',
                        right: '10px',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        cursor: 'pointer',
                        fontSize: '18px'
                      }}
                      onClick={() => {
                        setShowCalendarFin(!showCalendarFin);
                        if (isValidDateFormat(formData.fechaFin)) {
                          setCalendarDateFin(parseFechaInput(formData.fechaFin));
                        }
                      }}
                    >
                      📅
                    </span>
                  </div>
                  {showCalendarFin && generateCalendar(calendarDateFin, false)}
                </div>
              </div>

              <button
                type="submit"
                disabled={loading}
                style={{
                  width: '100%',
                  padding: '12px',
                  backgroundColor: loading ? '#95a5a6' : '#3498db',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '16px',
                  cursor: loading ? 'not-allowed' : 'pointer',
                  fontWeight: 'bold'
                }}
              >
                {loading ? '🔍 Buscando...' : '⚖️ Buscar Pesos PPPL'}
              </button>
            </form>
          </div>
        </div>

        {/* Error */}
        {error && (
          <div style={{ 
            padding: '20px', 
            backgroundColor: '#e74c3c', 
            color: 'white',
            borderRadius: '8px',
            marginBottom: '20px',
            textAlign: 'center'
          }}>
            <h3 style={{ margin: '0 0 10px 0' }}>❌ Error</h3>
            <p style={{ margin: 0, fontFamily: 'monospace' }}>{error}</p>
          </div>
        )}

        {/* Resultados */}
        {result && (
          <div style={{ marginTop: '20px' }}>
            {/* Header de resultados */}
            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              marginBottom: '20px',
              backgroundColor: 'white',
              padding: '15px 20px',
              borderRadius: '8px',
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
            }}>
              <div>
                <h2 style={{ color: '#2c3e50', margin: 0 }}>
                  ⚖️ Pesos por Lote Interno - PPPL
                </h2>
                <p style={{ color: '#7f8c8d', margin: '5px 0 0 0' }}>
                  {Array.isArray(result) ? `${Object.keys(groupedByInternalLot).length} lotes internos encontrados` : 'Datos recibidos'}
                </p>
              </div>
            </div>

            {/* Vista por Lotes Internos */}
            {Array.isArray(result) && result.length > 0 && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                {Object.entries(groupedByInternalLot).map(([internalLotId, lotItems], index) => {
                  const summary = getLotSummary(internalLotId, lotItems);
                  const isLoteExpanded = expandedLotes[internalLotId];
                  
                  if (!summary) return null;

                  return (
                    <div 
                      key={internalLotId}
                      style={{
                        backgroundColor: 'white',
                        borderRadius: '10px',
                        boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
                        overflow: 'hidden'
                      }}
                    >
                      {/* Header del Card */}
                      <div 
                        style={{
                          backgroundColor: '#27ae60',
                          color: 'white',
                          padding: '20px',
                          cursor: 'pointer'
                        }}
                        onClick={() => toggleLote(internalLotId)}
                      >
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <div style={{ flex: 1 }}>
                            <h3 style={{ margin: 0, fontSize: '20px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                              <span>{isLoteExpanded ? '▼' : '►'}</span>
                              <span>LOTE: {internalLotId}</span>
                            </h3>
                            <p style={{ margin: '8px 0 0 0', fontSize: '14px', opacity: 0.9 }}>
                              📦 {summary.totalCajillas} cajillas • 🏷️ {summary.productCode} • 👤 {summary.user}
                            </p>
                          </div>
                          <div style={{ textAlign: 'right', minWidth: '200px' }}>
                            <div style={{ fontSize: '24px', fontWeight: 'bold', marginBottom: '5px' }}>
                              {summary.totalPeso.toFixed(2)} kg
                            </div>
                            <div style={{ fontSize: '14px', opacity: 0.9 }}>Peso Total</div>
                          </div>
                        </div>
                      </div>

                      {/* Contenido del Card */}
                      {isLoteExpanded && (
                        <div style={{ padding: '0' }}>
                          {/* Información del Producto */}
                          <div style={{ 
                            padding: '20px',
                            backgroundColor: '#f8f9fa',
                            borderBottom: '1px solid #e0e0e0'
                          }}>
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '15px' }}>
                              <div>
                                <strong style={{ color: '#7f8c8d', fontSize: '12px' }}>Producto</strong>
                                <div style={{ fontWeight: 'bold', color: '#2c3e50', fontSize: '16px' }}>
                                  {summary.productName}
                                </div>
                              </div>
                              <div>
                                <strong style={{ color: '#7f8c8d', fontSize: '12px' }}>Código</strong>
                                <div style={{ fontWeight: 'bold', color: '#2c3e50', fontSize: '16px' }}>
                                  {summary.productCode}
                                </div>
                              </div>
                              <div>
                                <strong style={{ color: '#7f8c8d', fontSize: '12px' }}>Fecha Creación</strong>
                                <div style={{ color: '#2c3e50', fontSize: '14px' }}>
                                  {formatDate(summary.creationDate)}
                                </div>
                              </div>
                            </div>
                          </div>

                          {/* Detalle de Cajillas Pesadas */}
                          <div style={{ padding: '20px' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                              <h4 style={{ color: '#2c3e50', fontSize: '18px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                                <span>📦</span>
                                <span>Detalle de Cajillas ({summary.totalCajillas} cajillas)</span>
                              </h4>
                              <button
                                onClick={() => handlePrint(internalLotId, lotItems)}
                                style={{
                                  padding: '8px 16px',
                                  backgroundColor: '#e74c3c',
                                  color: 'white',
                                  border: 'none',
                                  borderRadius: '6px',
                                  cursor: 'pointer',
                                  fontWeight: 'bold',
                                  fontSize: '14px'
                                }}
                              >
                                🖨️ Imprimir Formato
                              </button>
                            </div>
                            
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(400px, 1fr))', gap: '15px' }}>
                              {lotItems.map((item, itemIndex) => (
                                <div 
                                  key={item.CD_CODIGO || itemIndex}
                                  style={{
                                    border: '2px solid #e0e0e0',
                                    borderRadius: '10px',
                                    overflow: 'hidden',
                                    backgroundColor: 'white'
                                  }}
                                >
                                  {/* Información de la Cajilla */}
                                  <div style={{ padding: '15px' }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '10px' }}>
                                      <div>
                                        <div style={{ fontWeight: 'bold', fontSize: '16px', color: '#2c3e50', marginBottom: '5px' }}>
                                          Cajilla #{item.CD_NUM_CAJILLA}
                                        </div>
                                        <div style={{ fontSize: '12px', color: '#7f8c8d' }}>
                                          Unidades: {item.CD_UNIDADES} • Vence: {formatSimpleDate(item.CD_FECHAVENCIMIENTO)}
                                        </div>
                                      </div>
                                      <div style={{ textAlign: 'center', minWidth: '80px' }}>
                                        <div style={{ 
                                          fontSize: '18px', 
                                          fontWeight: 'bold', 
                                          color: '#27ae60',
                                          backgroundColor: '#e8f5e8',
                                          padding: '8px 12px',
                                          borderRadius: '6px'
                                        }}>
                                          {item.CD_PESO?.toFixed(2)} kg
                                        </div>
                                        <div style={{ fontSize: '11px', color: '#7f8c8d', marginTop: '4px' }}>
                                          Peso
                                        </div>
                                      </div>
                                    </div>
                                    <div style={{ fontSize: '11px', color: '#95a5a6', borderTop: '1px solid #f0f0f0', paddingTop: '8px' }}>
                                      Pesado: {formatDate(item.CD_FECHACREACION)}
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            {/* Array vacío */}
            {Array.isArray(result) && result.length === 0 && (
              <div style={{ 
                padding: '40px', 
                textAlign: 'center',
                backgroundColor: 'white',
                borderRadius: '8px',
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
              }}>
                <div style={{ fontSize: '48px', marginBottom: '20px' }}>📭</div>
                <h3 style={{ color: '#7f8c8d' }}>No se encontraron productos PPPL</h3>
                <p style={{ color: '#95a5a6' }}>No hay datos de productos PPPL para las fechas especificadas</p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default BusquedaEtiquetasV2;